import { SafeImage } from "@/components/ui/SafeImage";
import { useMemo, useState, type ReactNode } from "react";
import { Calendar, Car, Fuel, Gauge, Mail, MapPin, Pencil, Settings, Star, Wrench } from "lucide-react";
import type { Car as CarType, ServiceHistory, User as UserType } from "@/types/database";
import { useCarServiceHistory, useCarMakeLogos, useUpdateCar, useUserByUserId, useUsers } from "@/lib/queries";
import { resolveCarMakeLogo, type CarMakeLogoEntry } from "@/lib/car-make-logos";
import { MakeLogo } from "@/components/cars/MakeModelGrid";
import { carImageSrc, carTitle, formatCurrency, formatDate, formatNumber, imageSrc, userDisplayName } from "@/lib/utils";
import { AdminPasswordConfirm } from "@/components/auth/AdminPasswordConfirm";
import { useAuth } from "@/contexts/AuthContext";
import { EditCarModal } from "@/components/cars/EditCarModal";
import { Button } from "@/components/ui/Button";
import { Field, Select } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";

function ServiceHistoryCard({ entry }: { entry: ServiceHistory }) {
  return (
    <article className="rounded-2xl bg-canvas p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold">Maintenance record</p>
          <p className="mt-1 text-xs text-muted">{formatDate(entry.serviceDate ?? entry.created_at)}</p>
        </div>
        {entry.totalCost != null && entry.totalCost > 0 ? (
          <p className="text-sm font-bold">{formatCurrency(entry.totalCost)}</p>
        ) : null}
      </div>
      {entry.serviceType?.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {entry.serviceType.map((type) => (
            <span key={type} className="stat-pill stat-pill-surface">
              {type}
            </span>
          ))}
        </div>
      ) : null}
      {entry.serviceList?.length ? (
        <p className="mt-3 text-sm text-muted">{entry.serviceList.join(" · ")}</p>
      ) : null}
    </article>
  );
}

function CarActionTile({
  title,
  description,
  icon,
  onClick,
}: {
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-full w-full flex-col justify-between rounded-2xl border border-white/20 bg-white/10 p-4 text-left text-white transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[var(--color-shift-green)] sm:p-5"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white">
        {icon}
      </span>
      <div>
        <p className="font-bold">{title}</p>
        <p className="mt-1 text-xs leading-snug text-white/85">{description}</p>
      </div>
    </button>
  );
}

function SpecCell({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-[#f5f5f7] p-4">
      <div className="mb-2 text-muted">{icon}</div>
      <p className="text-sm font-bold">{value}</p>
      <p className="mt-1 text-xs text-muted">{label}</p>
    </div>
  );
}

function CarDetailMakeLogo({
  car,
  makeLogos,
}: {
  car: CarType;
  makeLogos: Map<string, CarMakeLogoEntry> | undefined;
}) {
  const carLogoSrc = imageSrc(car.carLogo);
  const logoEntry = useMemo(
    () => (car.make && makeLogos ? resolveCarMakeLogo(car.make, makeLogos) : null),
    [car.make, makeLogos]
  );

  if (carLogoSrc) {
    return (
      <SafeImage
        src={carLogoSrc}
        alt={car.make ?? "Make"}
        width={56}
        height={56}
        className="h-14 w-auto max-w-[120px] object-contain"
      />
    );
  }

  return <MakeLogo make={car.make ?? "Car"} logo={logoEntry} className="h-14 w-auto max-w-[120px] object-contain" />;
}

function userInitials(user: UserType): string {
  const first = user.firstName?.trim().charAt(0) ?? "";
  const last = user.lastName?.trim().charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || "U";
}

function UserAvatar({ user, size = "md" }: { user: UserType; size?: "md" | "lg" }) {
  const profilePhoto = imageSrc(user.profilePhoto);
  const dimensions = size === "lg" ? "h-20 w-20 text-base" : "h-14 w-14 text-sm";

  if (profilePhoto) {
    return (
      <SafeImage
        src={profilePhoto}
        alt={userDisplayName(user)}
        width={size === "lg" ? 80 : 56}
        height={size === "lg" ? 80 : 56}
        className={`${dimensions} rounded-full object-cover`}
      />
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-accent-subtle font-bold text-accent-foreground ${dimensions}`}
    >
      {userInitials(user)}
    </div>
  );
}

function UserDetailRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value || value === "—") return null;

  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-3 last:border-0">
      <span className="shrink-0 text-muted">{label}</span>
      <span className="text-right font-semibold text-foreground">{value}</span>
    </div>
  );
}

function UserDetailModal({ user, open, onClose }: { user: UserType; open: boolean; onClose: () => void }) {
  return (
    <Modal open={open} onClose={onClose} title={userDisplayName(user)} className="max-w-lg">
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <UserAvatar user={user} size="lg" />
          <div className="min-w-0">
            <p className="text-lg font-bold text-foreground">{userDisplayName(user)}</p>
            {user.is_admin ? (
              <span className="mt-1 inline-block rounded-full bg-accent-subtle px-2.5 py-0.5 text-xs font-bold text-accent-foreground">
                Admin
              </span>
            ) : null}
            {user.email ? <p className="mt-1 truncate text-sm text-muted">{user.email}</p> : null}
          </div>
        </div>

        <div className="rounded-2xl bg-canvas px-4">
          <UserDetailRow label="Country" value={user.country} />
          <UserDetailRow label="Address" value={user.address} />
          <UserDetailRow label="Phone" value={user.phoneNumber} />
          <UserDetailRow label="Date of birth" value={formatDate(user.DOB)} />
          <UserDetailRow label="Member since" value={formatDate(user.created_at)} />
          <UserDetailRow
            label="Tokens"
            value={user.tokenAmount != null ? formatNumber(user.tokenAmount) : null}
          />
          <UserDetailRow
            label="VIN lookups"
            value={user.vinLookupAmount != null ? formatNumber(user.vinLookupAmount) : null}
          />
          <UserDetailRow label="User ID" value={user.userID} />
        </div>
      </div>
    </Modal>
  );
}

function CarOwnerSection({ car }: { car: CarType }) {
  const { verifyPassword } = useAuth();
  const { data: owner, isLoading, error } = useUserByUserId(car.userID);
  const { data: users = [] } = useUsers();
  const updateCarMutation = useUpdateCar();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assignPassword, setAssignPassword] = useState("");
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState(false);

  const userOptions = useMemo(
    () =>
      users
        .filter((user) => user.userID)
        .map((user) => ({
          value: user.userID as string,
          label: userDisplayName(user),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );

  async function handleAssignOwner() {
    if (!selectedUserId) return;
    if (!assignPassword.trim()) {
      setAssignError("Enter your password to confirm this change.");
      return;
    }

    setAssignError(null);
    setAssigning(true);
    try {
      const verifyError = await verifyPassword(assignPassword);
      if (verifyError) {
        setAssignError(verifyError);
        return;
      }

      await updateCarMutation.mutateAsync({
        carId: car.carID,
        updates: { userID: selectedUserId },
      });
      setSelectedUserId("");
      setAssignPassword("");
    } catch (e) {
      setAssignError(e instanceof Error ? e.message : "Failed to assign owner");
    } finally {
      setAssigning(false);
    }
  }

  const ownerActionPending = assigning || updateCarMutation.isPending;

  return (
    <div>
      <h2 className="mb-4 font-bold">Owner</h2>

      {!car.userID ? (
        <div className="space-y-3">
          <p className="text-sm text-muted">No owner linked to this vehicle.</p>
          <Field label="Assign owner">
            <Select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
              <option value="">Select a user</option>
              {userOptions.map((user) => (
                <option key={user.value} value={user.value}>
                  {user.label}
                </option>
              ))}
            </Select>
          </Field>
          <AdminPasswordConfirm
            password={assignPassword}
            onPasswordChange={setAssignPassword}
            disabled={ownerActionPending}
          />
          {assignError ? <p className="text-sm text-red-600">{assignError}</p> : null}
          <Button
            variant="brand"
            onClick={handleAssignOwner}
            disabled={!selectedUserId || !assignPassword.trim() || ownerActionPending}
          >
            {ownerActionPending ? "Assigning…" : "Assign owner"}
          </Button>
        </div>
      ) : isLoading ? (
        <div>
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 animate-pulse rounded-full bg-border/60" />
            <div className="space-y-2">
              <div className="h-4 w-32 animate-pulse rounded bg-border/60" />
              <div className="h-3 w-24 animate-pulse rounded bg-border/60" />
            </div>
          </div>
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">Failed to load owner details.</p>
      ) : owner ? (
        <>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="w-full rounded-2xl p-4 text-left transition hover:bg-canvas focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <div className="flex items-center gap-4">
              <UserAvatar user={owner} />

              <div className="min-w-0">
                <p className="truncate text-lg font-bold">{userDisplayName(owner)}</p>
                {owner.country ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{owner.country}</span>
                  </p>
                ) : null}
                {owner.email ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-muted">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{owner.email}</span>
                  </p>
                ) : null}
              </div>
            </div>
          </button>

          <UserDetailModal user={owner} open={modalOpen} onClose={() => setModalOpen(false)} />
        </>
      ) : (
        <p className="text-sm text-muted">Owner record not found for this vehicle.</p>
      )}
    </div>
  );
}

export function CarDetailPanel({ car }: { car: CarType }) {
  const [editOpen, setEditOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const { data: makeLogos } = useCarMakeLogos();
  const { data: serviceHistory = [], isLoading: serviceHistoryLoading } = useCarServiceHistory(
    car.carID,
    serviceOpen
  );

  return (
    <Workspace>
      <WorkspaceSection className="grid gap-0 lg:grid-cols-[1fr_1.1fr]">
        {/* Left — info */}
        <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <CarDetailMakeLogo car={car} makeLogos={makeLogos} />
          <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground sm:text-2xl">
            {[car.make, car.model].filter(Boolean).join(" ") || "Unknown car"}
          </h2>
          {car.modelYear || car.fuelTypePrimary || car.transmission ? (
            <p className="mt-1 flex flex-wrap items-center gap-x-2.5 text-sm text-muted">
              {car.modelYear ? <span>{car.modelYear}</span> : null}
              {car.modelYear && (car.fuelTypePrimary ?? car.transmission) ? (
                <span className="h-3.5 w-px bg-border" aria-hidden />
              ) : null}
              {car.fuelTypePrimary ?? car.transmission ? (
                <span>{car.fuelTypePrimary ?? car.transmission}</span>
              ) : null}
            </p>
          ) : null}

          {car.value ? (
            <h1 className="mt-4 text-xl font-bold tracking-tight sm:text-2xl">
              {formatCurrency(car.value)}
              <span className="text-sm font-semibold text-muted"> /value</span>
            </h1>
          ) : null}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {car.shiftRating != null ? <span className="stat-pill"><Star className="h-3.5 w-3.5 fill-accent text-accent" />{car.shiftRating}</span> : null}
            {car.odometer != null ? <span className="stat-pill"><Gauge className="h-3.5 w-3.5" />{formatNumber(car.odometer)} mi</span> : null}
            {car.transmission ? <span className="stat-pill"><Settings className="h-3.5 w-3.5" />{car.transmission}</span> : null}
            {car.licensePlate ? <span className="stat-pill">{car.licensePlate}</span> : null}
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3">
            <SpecCell icon={<Car className="h-5 w-5" />} label="Body" value={car.body ?? "—"} />
            <SpecCell icon={<Fuel className="h-5 w-5" />} label="Fuel" value={car.fuelTypePrimary ?? "—"} />
            <SpecCell icon={<Settings className="h-5 w-5" />} label="Engine" value={car.enginePowerHP ? `${car.enginePowerHP} HP` : "—"} />
            <SpecCell icon={<Calendar className="h-5 w-5" />} label="Year" value={car.modelYear?.toString() ?? "—"} />
          </div>
        </div>

        {/* Right — image */}
        <div className="relative aspect-[16/9] w-full bg-[#fafafa] lg:aspect-auto lg:min-h-[300px] lg:h-full">
          <SafeImage
            src={carImageSrc(car.carImage)}
            alt={carTitle(car)}
            fill
            className="object-cover"
            sizes="(max-width: 1024px) 100vw, 50vw"
            priority
          />
        </div>
      </WorkspaceSection>

      <WorkspaceSection border className="grid gap-0 lg:grid-cols-[1fr_1.1fr] lg:items-stretch">
        <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
          <CarOwnerSection car={car} />
        </div>

        <div className="flex h-full min-h-full flex-col bg-[var(--color-shift-green)]">
          <div className="p-4 pb-3 sm:p-5 sm:pb-3">
            <h2 className="font-bold text-white">Actions</h2>
          </div>
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 px-4 pb-4 sm:px-5 sm:pb-5">
            <CarActionTile
              title="Service history"
              description="View maintenance records."
              icon={<Wrench className="h-4 w-4" />}
              onClick={() => setServiceOpen(true)}
            />
            <CarActionTile
              title="Edit record"
              description="Make, model, specs, and ratings."
              icon={<Pencil className="h-4 w-4" />}
              onClick={() => setEditOpen(true)}
            />
          </div>

          <Modal
            open={serviceOpen}
            onClose={() => setServiceOpen(false)}
            title="Service history"
            className="max-w-lg"
            footer={
              <Button variant="brand" onClick={() => setServiceOpen(false)}>
                Close
              </Button>
            }
          >
            <p className="mb-4 text-sm text-muted">Maintenance records are read-only.</p>
            {serviceHistoryLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-24 animate-pulse rounded-2xl bg-canvas" />
                ))}
              </div>
            ) : serviceHistory.length === 0 ? (
              <p className="text-sm text-muted">No service records for this vehicle yet.</p>
            ) : (
              <div className="space-y-3 text-foreground">
                {serviceHistory.map((entry) => (
                  <ServiceHistoryCard key={`history-${entry.id}`} entry={entry} />
                ))}
              </div>
            )}
          </Modal>

          <EditCarModal car={car} open={editOpen} onClose={() => setEditOpen(false)} />
        </div>
      </WorkspaceSection>
    </Workspace>
  );
}
